import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MoreHorizontal, RefreshCw, Plug, ServerCog, Pause, Play, PlayCircle, Trash2, HardDrive } from "lucide-react";

import { ServersApi, OpsApi, GroupsApi, AssignmentApi, type ServerRow, type DiskInfo, type GroupRow } from "@/lib/services";
import { fa } from "@/locales/fa";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/stores/auth";
import { num, timeAgo } from "@/lib/utils";
import { AddServerDialog } from "@/components/servers/AddServerDialog";
import { DiskCell } from "@/components/servers/DiskCell";

export function ServersPage() {
  const [q, setQ] = useState("");
  const { data, isLoading, isError } = useQuery({
    queryKey: ["servers"],
    queryFn: ServersApi.list,
    refetchInterval: 20_000,
  });

  const rows = (data?.items ?? []).filter((s) =>
    !q ? true : s.name.toLowerCase().includes(q.toLowerCase()) || s.host.toLowerCase().includes(q.toLowerCase())
  );

  const { data: disks } = useQuery({
    queryKey: ["disks"],
    queryFn: ServersApi.listDisks,
    refetchInterval: 60_000,
  });

  const { data: groups } = useQuery({ queryKey: ["groups"], queryFn: GroupsApi.list });
  const { data: serverGroups } = useQuery({
    queryKey: ["server-groups"],
    queryFn: AssignmentApi.listServerGroups,
  });
  // serverId → groupId currently assigned via a server_id rule.
  const serverGroupMap = new Map((serverGroups?.items ?? []).map((i) => [i.serverId, i.groupId]));

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between overflow-hidden">
        <div className="shrink-0">
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <ServerCog className="size-5 text-primary" />
            {fa.servers.title}
          </h1>
          <p className="text-sm text-muted-foreground">فهرست سرورهای cPanel متصل به آف‌سایت</p>
        </div>
        <div className="flex items-center gap-2 min-w-0 flex-1 sm:justify-end">
          <Input
            dir="rtl"
            placeholder={fa.common.search}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="min-w-0 flex-1 sm:max-w-[200px]"
          />
          <AddServerDialog />
        </div>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-muted-foreground">
            مجموع: <span className="text-foreground">{num(rows.length)}</span> سرور
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : isError ? (
            <div className="py-10 text-center text-muted-foreground">{fa.common.error}</div>
          ) : rows.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground">{fa.common.empty}</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{fa.servers.host}</TableHead>
                  <TableHead>{fa.servers.agent}</TableHead>
                  <TableHead className="hidden xl:table-cell">{fa.servers.diskTarget}</TableHead>
                  <TableHead className="hidden lg:table-cell">گروه بکاپ</TableHead>
                  <TableHead className="text-center">{fa.servers.assignedAccounts}</TableHead>
                  <TableHead className="hidden sm:table-cell">آخرین تماس</TableHead>
                  <TableHead className="text-center">فعال</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((s) => (
                  <ServerRowItem
                    key={s.id}
                    server={s}
                    disks={disks ?? []}
                    groups={groups ?? []}
                    currentGroupId={serverGroupMap.get(s.id) ?? null}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ServerRowItem({
  server,
  disks,
  groups,
  currentGroupId,
}: {
  server: ServerRow;
  disks: DiskInfo[];
  groups: GroupRow[];
  currentGroupId: string | null;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  // RBAC — drain/wipe + bulk are operator-level, wipe specifically needs the
  // strongest perm so a misclicked button can't blow away an entire server.
  const canKill = useAuth((s) => s.hasPerm("ops.killswitch"));
  const canTrigger = useAuth((s) => s.hasPerm("job.trigger"));
  const canGroup = useAuth((s) => s.hasPerm("account.group.manage"));
  const canDestroy = useAuth((s) => s.hasPerm("server.destroy"));
  const canMigrate = useAuth((s) => s.hasPerm("ops.migrate"));
  const [wipeOpen, setWipeOpen] = useState(false);
  const [migrateOpen, setMigrateOpen] = useState(false);

  const test = useMutation({
    mutationFn: () => ServersApi.test(server.id),
    onSuccess: (res) => {
      if (res.ok) toast.success(`ایجنت پاسخگو است — ${res.agentVersion ?? ""}`, { description: `${res.latencyMs ?? 0} ms` });
      else toast.error("ایجنت پاسخ نداد", { description: res.error });
    },
    onError: (e: Error) => toast.error("خطا در تست", { description: e.message }),
  });
  const sync = useMutation({
    mutationFn: () => ServersApi.sync(server.id),
    onSuccess: (r) => toast.success(`${num(r.enqueued)} اکانت همگام‌سازی شد`),
    onError: (e: Error) => toast.error("خطا در همگام‌سازی", { description: e.message }),
  });
  // Switch toggles `active ⇄ paused`. We deliberately key off `state === "active"`
  // (not `enabled`) because `enabled` is true for both `active` AND `paused`
  // (it only goes false when state is `disabled`). Using `enabled` made the
  // switch ON for a paused server and then sending `{enabled:false}` would
  // map back to paused — so the switch looked stuck. Now ON = active, OFF
  // = paused, and you can flip freely. Hard-disable ("disabled" state) still
  // lives behind the "wipe server" dialog.
  const toggle = useMutation({
    mutationFn: (active: boolean) => ServersApi.patch(server.id, { enabled: active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["servers"] }),
    onError: (e: Error) => toast.error("ناموفق", { description: e.message }),
  });
  const drain = useMutation({
    mutationFn: () =>
      server.state === "paused" ? OpsApi.undrain(server.id) : OpsApi.drain(server.id),
    onSuccess: (r) => {
      toast.success(r.state === "paused" ? fa.servers.drainedNow : fa.servers.undrainedNow);
      qc.invalidateQueries({ queryKey: ["servers"] });
    },
    onError: (e: Error) => toast.error("ناموفق", { description: e.message }),
  });
  const bulk = useMutation({
    mutationFn: () =>
      OpsApi.bulkBackup({ serverId: server.id, kind: "account_full", limit: 2000 }),
    onSuccess: (r) =>
      toast.success(fa.ops.bulkBackup.submitted(r.queued, r.skipped), {
        description: r.errors.length > 0 ? `${r.errors.length} خطا` : undefined,
      }),
    onError: (e: Error) => toast.error("ناموفق", { description: e.message }),
  });
  const setGroup = useMutation({
    mutationFn: (groupId: string | null) => AssignmentApi.setServerGroup(server.id, groupId),
    onSuccess: (r) => {
      toast.success(
        r.groupId ? `گروه سرور ${server.name} تنظیم شد` : `قانون گروه سرور ${server.name} برداشته شد`,
        { description: `${num(r.updated)} اکانت جابه‌جا شد` },
      );
      qc.invalidateQueries({ queryKey: ["server-groups"] });
      qc.invalidateQueries({ queryKey: ["groups"] });
      qc.invalidateQueries({ queryKey: ["accounts"] });
    },
    onError: (e: Error) => toast.error("ناموفق", { description: e.message }),
  });
  const wipe = useMutation({
    mutationFn: (confirm: string) => OpsApi.wipeServer(server.id, confirm),
    onSuccess: (r) => {
      toast.success(fa.servers.wipeSuccess(r.snapshotsPruned));
      qc.invalidateQueries({ queryKey: ["servers"] });
      qc.invalidateQueries({ queryKey: ["snapshots"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      setWipeOpen(false);
    },
    onError: (e: Error) => toast.error("ناموفق", { description: e.message }),
  });
  const migrate = useMutation({
    mutationFn: (toDisk: string) => OpsApi.migrateDisk(server.id, toDisk),
    onSuccess: (r) => {
      if (r.ok) {
        toast.success(
          `repo "${r.serverName}" منتقل شد به ${r.toDisk}`,
          {
            description: `${(r.bytesMoved / (1024 * 1024 * 1024)).toFixed(1)} GB در ${(r.ms / 1000).toFixed(0)}s${
              r.warnings.length ? ` · هشدار: ${r.warnings[0]}` : ""
            }`,
          },
        );
      } else {
        toast.error("انتقال ناموفق", { description: r.error ?? "خطای نامشخص" });
      }
      qc.invalidateQueries({ queryKey: ["servers"] });
      qc.invalidateQueries({ queryKey: ["disks"] });
      setMigrateOpen(false);
    },
    onError: (e: Error) => toast.error("ناموفق", { description: e.message }),
  });

  return (
    <TableRow
      className="cursor-pointer hover:bg-muted/40 transition-colors"
      onClick={(e) => {
        const target = e.target as HTMLElement;
        if (target.closest("button, [role=menu], [role=menuitem], [role=switch], input")) return;
        navigate(`/servers/${server.id}`);
      }}
    >
      <TableCell>
        <div className="flex flex-col">
          <span className="font-medium">{server.name}</span>
          <span className="text-[11px] text-muted-foreground lat-num">{server.host}:{server.port}</span>
        </div>
      </TableCell>
      <TableCell>
        <Badge variant={server.online ? "success" : "destructive"}>
          {server.online ? fa.servers.online : fa.servers.offline}
        </Badge>
        {server.agentVersion && (
          <span className="ms-2 text-[11px] text-muted-foreground lat-num">v{server.agentVersion}</span>
        )}
      </TableCell>
      <TableCell className="hidden xl:table-cell">
        <DiskCell server={server} disks={disks} />
      </TableCell>
      <TableCell className="hidden lg:table-cell">
        <Select
          value={currentGroupId ?? "__default__"}
          disabled={!canGroup || setGroup.isPending}
          onValueChange={(v) => setGroup.mutate(v === "__default__" ? null : v)}
        >
          <SelectTrigger className="h-8 w-auto min-w-[120px] text-xs">
            <SelectValue placeholder="—" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__default__">گروه پیش‌فرض</SelectItem>
            {groups
              .filter((g) => !g.isDefault)
              .map((g) => (
                <SelectItem key={g.id} value={g.id}>
                  {g.name}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell className="text-center tabular">{num(server.accountCount)}</TableCell>
      <TableCell className="text-muted-foreground hidden sm:table-cell">
        {server.lastSeenAt ? timeAgo(server.lastSeenAt) : "—"}
      </TableCell>
      <TableCell className="text-center">
        <Switch
          checked={server.state === "active"}
          disabled={server.state === "disabled" || toggle.isPending}
          onCheckedChange={(v) => toggle.mutate(v)}
        />
      </TableCell>
      <TableCell>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon"><MoreHorizontal className="size-4" /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>{fa.common.actions}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => test.mutate()}>
              <Plug className="size-4" /> {fa.servers.pingAgent}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => sync.mutate()}>
              <RefreshCw className="size-4" /> همگام‌سازی اکانت‌ها
            </DropdownMenuItem>

            <DropdownMenuSeparator />
            {/* Drain / Undrain — soft pause that lets in-flight jobs finish
                but refuses new ones at the dispatcher. Distinct from the
                hard `enabled` switch above (which is `state=disabled` and
                drops the server from heartbeats/inventory too). */}
            {server.state === "paused" ? (
              <DropdownMenuItem
                onSelect={() => drain.mutate()}
                disabled={!canKill || drain.isPending}
              >
                <Play className="size-4" /> {fa.servers.undrain}
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                onSelect={() => drain.mutate()}
                disabled={!canKill || drain.isPending}
              >
                <Pause className="size-4" /> {fa.servers.drain}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onSelect={() => {
                if (
                  confirm(
                    `همه اکانت‌های ${server.name} (${num(server.accountCount)}) برای بکاپ فول صف می‌شوند. ادامه؟`,
                  )
                ) {
                  bulk.mutate();
                }
              }}
              disabled={!canTrigger || bulk.isPending}
            >
              <PlayCircle className="size-4" /> {fa.servers.bulkBackupServer}
            </DropdownMenuItem>

            <DropdownMenuItem
              onSelect={() => setMigrateOpen(true)}
              disabled={!canMigrate}
            >
              <HardDrive className="size-4" /> انتقال ریپو به دیسک دیگر
            </DropdownMenuItem>

            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => setWipeOpen(true)}
              disabled={!canDestroy}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="size-4" /> {fa.servers.wipeServer}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>

      <WipeServerDialog
        open={wipeOpen}
        onOpenChange={setWipeOpen}
        server={server}
        onConfirm={(name) => wipe.mutate(name)}
        pending={wipe.isPending}
      />
      <MigrateDiskDialog
        open={migrateOpen}
        onOpenChange={setMigrateOpen}
        server={server}
        onConfirm={(d) => migrate.mutate(d)}
        pending={migrate.isPending}
      />
    </TableRow>
  );
}

// Plan §9.9 — "انتقال ریپو به دیسک دیگر". The destination disk dropdown is
// populated from /ops/disks (live mountpoint + free space). The backend
// requires zero queued/running jobs for this server before allowing the
// move, plus pauses the server during the rsync. We surface those gates as
// inline hints rather than relying on backend errors.
function MigrateDiskDialog({
  open,
  onOpenChange,
  server,
  onConfirm,
  pending,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  server: ServerRow;
  onConfirm: (toDisk: string) => void;
  pending: boolean;
}) {
  const [toDisk, setToDisk] = useState("");
  const disks = useQuery({
    queryKey: ["disks"],
    queryFn: () => OpsApi.listDisks(),
    enabled: open,
  });
  const currentDisk = server.diskTargetId;
  const candidates = (disks.data ?? []).filter((d) => d.id !== currentDisk);

  return (
    <Dialog open={open} onOpenChange={(o) => { setToDisk(""); onOpenChange(o); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HardDrive className="size-4 text-primary" />
            انتقال ریپو به دیسک دیگر
          </DialogTitle>
          <DialogDescription className="text-xs leading-relaxed">
            ریپوی restic سرور <span className="font-mono lat-num" dir="ltr">{server.name}</span> از{" "}
            <span className="font-mono lat-num" dir="ltr">{currentDisk ?? "—"}</span> به دیسک
            دیگری منتقل می‌شود. سرور موقتاً <code>paused</code> می‌شود، فایل‌ها
            با <code>mv</code>/<code>rsync</code> منتقل، symlink swing می‌شود،
            و سپس <code>state</code> برمی‌گردد. برای ریپوی چند صد گیگابایتی
            ممکن است تا 30 دقیقه طول بکشد.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-2">
          <Label>دیسک مقصد</Label>
          <Select value={toDisk} onValueChange={setToDisk}>
            <SelectTrigger>
              <SelectValue placeholder="انتخاب کنید" />
            </SelectTrigger>
            <SelectContent>
              {candidates.length === 0 ? (
                <SelectItem value="__none__" disabled>
                  هیچ دیسک دیگری موجود نیست
                </SelectItem>
              ) : (
                candidates.map((d) => {
                  const freeGB = (d.freeBytes / (1024 ** 3)).toFixed(0);
                  const totalGB = (d.sizeBytes / (1024 ** 3)).toFixed(0);
                  return (
                    <SelectItem key={d.id} value={d.id}>
                      <span className="lat-num" dir="ltr">{d.id}</span>
                      <span className="ms-2 text-muted-foreground text-xs lat-num" dir="ltr">
                        {freeGB} GB free / {totalGB} GB
                      </span>
                    </SelectItem>
                  );
                })
              )}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter className="mt-2">
          <Button
            disabled={!toDisk || pending}
            onClick={() => onConfirm(toDisk)}
          >
            {pending ? "در حال انتقال…" : "شروع انتقال"}
          </Button>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {fa.common.cancel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Destructive: the operator must type the server name EXACTLY to confirm.
// Plan §13 — "Wipe Server Backups: تایپ کردن دقیق نام سرور (مثل cip1) برای
// تأیید". This frontend check is purely a guardrail — the backend
// /ops/wipe-server/:id route enforces the same check server-side.
function WipeServerDialog({
  open,
  onOpenChange,
  server,
  onConfirm,
  pending,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  server: ServerRow;
  onConfirm: (name: string) => void;
  pending: boolean;
}) {
  const [typed, setTyped] = useState("");
  const matches = typed.trim() === server.name;
  return (
    <Dialog open={open} onOpenChange={(o) => { setTyped(""); onOpenChange(o); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-destructive">{fa.servers.wipeServer}</DialogTitle>
          <DialogDescription>{fa.servers.wipePrompt(server.name)}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-2">
          <Label>نام دقیق سرور</Label>
          <Input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={server.name}
            dir="ltr"
            className="lat-num font-mono"
            autoFocus
          />
        </div>
        <DialogFooter className="mt-2">
          <Button
            variant="destructive"
            disabled={!matches || pending}
            onClick={() => onConfirm(server.name)}
          >
            {pending ? "در حال حذف…" : `${fa.servers.wipeServer} (دائمی)`}
          </Button>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {fa.common.cancel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
